/**
 * Shipping rates API endpoint schemas
 *
 * Zod schemas and types for /api/checkout/shipping-rates
 */
import { z } from 'zod';
import { TaxEstimateSchema } from './tax-estimate';

// Request schema
export const ShippingRatesQuerySchema = z.object({
  listingId: z.coerce.number().int().positive(),
  /**
   * For CJ Dropshipping listings only: the CJ variant id (vid) the buyer
   * picked on the product page. CJ's freight calculator returns
   * variant-specific dimensions/weight, so this changes the rate.
   * Optional — when omitted, the cj-shipping helper falls back to the
   * cheapest variant in LISTINGS_METADATA.externalData.
   */
  variantVid: z.string().optional(),
});

export type ShippingRatesQuery = z.infer<typeof ShippingRatesQuerySchema>;

// Response component schemas
export const ServiceLevelSchema = z.object({
  name: z.string(),
  token: z.string(),
  terms: z.string(),
});

export type ServiceLevel = z.infer<typeof ServiceLevelSchema>;

export const ShippingRateSchema = z.object({
  id: z.string(),
  provider: z.string(),
  servicelevel: ServiceLevelSchema,
  amount: z.string(),
  currency: z.string(),
  estimatedDays: z.number().nullable(),
  durationTerms: z.string().nullable(),
  shipsFrom: z.string().optional(),
  /** The sales tax the order would draw with this rate's freight. */
  tax: TaxEstimateSchema.optional(),
});

export type ShippingRate = z.infer<typeof ShippingRateSchema>;

// Response schemas
export const ShippingRatesResponseSchema = z.object({
  success: z.literal(true),
  rates: z.array(ShippingRateSchema),
  isDefault: z.boolean().optional(),
  shipmentId: z.string().optional(),
  /** Cross-border order: no carrier rate, the two parties arrange shipping. */
  arrangedSeparately: z.boolean().optional(),
});

export type ShippingRatesResponse = z.infer<typeof ShippingRatesResponseSchema>;

export const ShippingRatesErrorSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export type ShippingRatesError = z.infer<typeof ShippingRatesErrorSchema>;

/**
 * Combined response schema for shipping rates
 * Discriminated union of success and error responses
 */
export const ShippingRatesCombinedResponseSchema = z.discriminatedUnion('success', [
  ShippingRatesResponseSchema,
  ShippingRatesErrorSchema,
]);

export type ShippingRatesCombinedResponse = z.infer<typeof ShippingRatesCombinedResponseSchema>;
